/**
 * Whether a phone browser should be offered the app (the owner's ask,
 * 2026-10-05): *"if app.torik.ai is opened in android, ios we should display
 * offer to install app."* Pure, so the rule is testable without a browser —
 * the caller passes the runtime mode, whether this bundle runs inside the
 * packaged app, the user agent, and the store links; the answer says whether
 * to offer, for which platform, and to which link.
 *
 * The guards, each for a reason:
 *   * only the public web — `mode` is `cloud` on app.torik.ai and not on the
 *     hub's own UI (`local`), the desktop shell, or the packaged app's WebView,
 *     which loads this same bundle and must never offer itself (`nativeApp`);
 *   * Android / iOS only — a desktop browser has no store on the other end;
 *   * a platform with no link configured is not offered at all (the doors go
 *     live one at a time — `config/appStores.ts`).
 */

export interface InstallOfferInput {
  /** Runtime mode (`config/runtime.ts`): the offer belongs to `cloud` only. */
  mode: string
  /** True inside the packaged app (Capacitor), whose WebView loads this bundle too. */
  nativeApp: boolean
  userAgent: string
  links: { android: string; ios: string }
}

export type InstallOffer =
  | { show: false; platform: null; url: '' }
  | { show: true; platform: 'android' | 'ios'; url: string }

export function installOfferFor(input: InstallOfferInput): InstallOffer {
  if (input.mode !== 'cloud' || input.nativeApp) return { show: false, platform: null, url: '' }

  const userAgent = input.userAgent.toLowerCase()
  const platform = /android/.test(userAgent) ? 'android' : /iphone|ipad|ipod/.test(userAgent) ? 'ios' : null
  if (!platform) return { show: false, platform: null, url: '' }

  const url = input.links[platform]
  if (!url) return { show: false, platform: null, url: '' }

  return { show: true, platform, url }
}
